import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { workShiftReminderActions } from 'entities/salary/work-shifts/work-shift-reminder.slice';
import { workShiftsApi } from 'features/salary/work-shifts/api';
import { Link } from 'react-router-dom';
import { useActionCreators } from 'shared/store';
import { useSnack } from 'shared/ui/snack';
import { salaryLinkManager } from 'utils/link-manager';

export const WorkShiftSummary = () => {
  const { data } = workShiftsApi.useGetTodayWorkShiftPlanQuery(undefined, { refetchOnMountOrArgChange: true });
  const [openShift, { isLoading: isOpening }] = workShiftsApi.useOpenWorkShiftMutation();
  const { openReminder } = useActionCreators(workShiftReminderActions);
  const { showError, showSuccess } = useSnack();

  const shift = data?.shift;
  if (!data) return null;

  const callsPlan = shift?.callsPlan ?? data.callsPlan;
  const offersPlan = shift?.offersPlan ?? data.offersPlan;
  const totalPlan = callsPlan + offersPlan;
  const totalFact = Math.min(data.callsFact, callsPlan) + Math.min(data.offersFact, offersPlan);
  const progress = totalPlan ? Math.min(100, Math.round((totalFact / totalPlan) * 100)) : 0;
  const isPlanCompleted = totalPlan > 0 && data.callsFact >= callsPlan && data.offersFact >= offersPlan;
  const workShiftsLink = `/${salaryLinkManager.baseUrl}/${salaryLinkManager.tabs.workShifts}`;

  const handleStart = async () => {
    try {
      const newShift = await openShift().unwrap();
      showSuccess('План на день открыт');
      openReminder(newShift.date);
    } catch (e) {
      showError('Ошибка открытия плана на день', e);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Box>
          <Typography variant="h6">План на день</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Звонки и коммерческие предложения
          </Typography>
        </Box>
        <Stack direction="row" spacing={{ xs: 3, sm: 5 }} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap sx={{ flexGrow: 1, py: 2 }}>
          <CircularPercent value={progress} />
          <Box sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 400 }}>Звонки {data.callsFact} / {callsPlan || '-'}</Typography>
            <Typography sx={{ mt: 1, fontSize: { xs: 20, sm: 24 }, fontWeight: 400 }}>КП {data.offersFact} / {offersPlan || '-'}</Typography>
            {shift?.closedAt && <Chip size="small" color="success" label="Закрыт" sx={{ mt: 1 }} />}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
          {!shift && (
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={handleStart} disabled={isOpening || totalPlan === 0}>
              Старт
            </Button>
          )}
          {shift && !shift.closedAt && (
            <>
              <Button onClick={() => openReminder(shift.date)} endIcon={<KeyboardDoubleArrowRightIcon />}>
                Подробнее
              </Button>
              {isPlanCompleted && (
                <Button variant="contained" color="success" component={Link} to={workShiftsLink} endIcon={<KeyboardDoubleArrowRightIcon />}>
                  Закрыть
                </Button>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

const CircularPercent = ({ value }: { value: number }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0, width: { xs: 160, sm: 192 }, height: { xs: 160, sm: 192 } }}>
    <CircularProgress
      variant="determinate"
      value={100}
      size="100%"
      thickness={6}
      sx={{ color: (theme) => theme.palette.action.hover }}
    />
    <CircularProgress
      variant="determinate"
      value={value}
      size="100%"
      thickness={6}
      color={value >= 100 ? 'success' : 'primary'}
      sx={{ position: 'absolute', left: 0 }}
    />
    <Box sx={{ inset: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: { xs: 32, sm: 40 }, fontWeight: 500 }}>{value}%</Typography>
    </Box>
  </Box>
);
